import React, { useState, useEffect } from 'react';
import { resetUrl, getUrl } from '../../../common/reset-image';
// @ts-ignore
import ReactImageMagnify from 'react-image-magnify';
import './index.less';
import LcpVideo from '../lcp_video';
import DetailIcon from '../../../common/detail-icon';
import doLog from '../../../common/log';
import SkuStore from '@/stores/sku';

interface PropTypes {
  activeId: number;
  setActiveId: (i: number) => void;
  mediaItems: [Partial<ImageInterface>];
  setDialogVisible: (i: boolean) => void;
  productId: number;
  i18n: DetailTypes.i18n;
  isFavorite: boolean;
  abtest: DetailTypes.Abtest;
  handleFavorite: () => void;
}

export default function MainImage(props: PropTypes) {
  const {
    i18n,
    activeId,
    setActiveId,
    mediaItems,
    setDialogVisible,
    isFavorite,
    handleFavorite,
    abtest,
  } = props;
  const [isHovering, setIsHovering] = useState(false);
  const [reload, setReload] = useState(false);
  const skuInfo = SkuStore((state) => state.product);
  const originImage = skuInfo?.skuImageReview?.originImage;
  // 商品主图切换的item
  const currentItem = mediaItems[activeId];
  const imgSrc = getUrl(currentItem);

  //  sku面板优化实验桶标识
  const isOptimizedSku = abtest?.pc_detail_fantasy_sku_optimized?.indexOf('sku_optimized') > 0;
  // 最后一阶的SkuItem内容，用来看sku面板外透还是出弹窗，（sku items length>8或者sku阶数大于3才出弹窗）
  // noSkuDialog是sku面板外透还是出弹窗的标识，为true的时候不出弹窗
  const noSkuDialog =
    isOptimizedSku &&
    skuInfo?.attr[skuInfo.attr.length - 1]?.values?.length <= 8 &&
    skuInfo?.attr.length <= 3;
  // 如果sku为true，只展示sku图片，用detail-sku-image容器装
  const isSku = noSkuDialog && skuInfo?.skuShowType === 'sku';
  // 渲染skuImg的标识
  const renderSkuImg = isSku && !!originImage;

  // 手动首屏触发打点
  const handleImageLoad = (e: Event) => {
    const target = e.target as HTMLImageElement;
    const bb = (window as any).__BB || ((window as any).__BB = {});
    bb.mode = -1;
    bb.exposed = new Date();
    // 替换成原始图片
    setTimeout(() => {
      target.src = `${resetUrl(target.src, '1200')}?avif=close` || target.src;
      setReload(true);
    }, 2 * 1000);
    target.removeEventListener('load', handleImageLoad);
  };

  useEffect(() => {
    const mainImg = document.querySelector('img.detail-main-img') as HTMLImageElement;
    if (!mainImg) return;
    mainImg.addEventListener('load', handleImageLoad);
    return () => {
      mainImg.removeEventListener('load', handleImageLoad);
    };
  }, []);

  // 渲染主图部分，包括商品图片和sku图片
  const renderCustom = () => {
    // 渲染sku图片
    if (isSku && originImage) {
      return (
        <div className="detail-sku-image">
          <img src={resetUrl(originImage)} alt="sku-image" />
        </div>
      );
    }
    // 渲染商品主图放大组件
    return (
      <div
        className="detail-main-image-outside"
        onClick={(e) => {
          e.preventDefault();
          doLog('/sc.details.main_image_expand');
          setDialogVisible(true);
        }}
      >
        <ReactImageMagnify
          {...{
            className: 'detail-main-img-container',
            imageClassName: 'detail-main-img',
            enlargedImagePosition: 'over',
            smallImage: {
              isFluidWidth: true,
              // 初次加载压缩图片，onload后1200原图
              src:
                activeId === 0 && !reload
                  ? resetUrl(imgSrc, '720', 'q50')
                  : `${resetUrl(imgSrc, '1200')}?avif=close`,
            },
            largeImage: {
              src: `${resetUrl(imgSrc, '1200')}?avif=close`,
              width: 960,
              height: 960,
            },
          }}
        />
      </div>
    );
  };

  // 真人接待
  const renderLive = () => {
    if (mediaItems.findIndex((item: any) => item.type === 'live') !== -1) {
      return (
        // 不能改id名，直播组件资源会在id为‘live-root’的div里插入直播组件
        <div
          id="live-root"
          style={{
            display: currentItem.type === 'live' ? '' : 'none',
          }}
        >
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setDialogVisible(true);
            }}
          >
            <img className="detail-live-root-info" src={resetUrl(getUrl(currentItem), '960')} />
          </a>
        </div>
      );
    }
    return null;
  };

  // 判断鼠标是否进入主图区域
  const handleMouseEnter = () => {
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  //展示主图or视频/真人直播
  const chkStates = () => {
    // 优先展示sku图片
    if (renderSkuImg) {
      return 'flex';
    }
    if (currentItem.type === 'live') {
      return 'none';
    }
    if (currentItem.type === 'video') {
      return 'none';
    }
    return 'flex';
  };

  // 用来控制是否展示主图下方的引导词，只有商品主图才有引导词，video、live、sku图片都没有
  const visGuideWords = () => {
    if (isSku || currentItem.type === 'video' || currentItem.type === 'live') return false;
    return true;
  };
  /**
   * if(noSkuDialog) 才需要判断是否isSku
   * 如果 !noSkuDialog,一定是有下面的渲染逻辑的
   */
  const renderVideoAndLive = () => {
    return (
      <>
        {mediaItems.filter((i: any) => i.type === 'video').length > 0 && (
          <LcpVideo item={currentItem} abtest={abtest} renderSkuImg={renderSkuImg} />
        )}
        {renderLive()}
      </>
    );
  };

  if (!currentItem) return null;
  return (
    <div className="main-img-container">
      {/* 直播组件监听main-index出小窗 */}
      <div className="main-index">
        {/* 渲染视频和真人直播*/}
        {renderVideoAndLive()}

        {/* 渲染主图 */}
        <div
          className="image-view"
          style={{
            display: chkStates(),
          }}
          onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {renderCustom()}
        </div>
        {/* 主图的左右切换按钮 */}
        <div className={'arrow-link arrow-link-left-mask'}></div>
        <div className={'arrow-link arrow-link-right-mask'}></div>
        <div
          className={`arrow-link arrow-link-button arrow-link-left ${
            activeId === 0 ? 'disabled' : ''
          }`}
          onClick={(e) => {
            e.preventDefault();
            doLog(`/sc.details.main_image_swipe_${activeId - 1}`);
            setActiveId(activeId - 1);
          }}
        >
          <DetailIcon className="detail-icon-left" symbol="icon-left" width="30" height="30" />
        </div>
        <div
          className={`arrow-link arrow-link-button arrow-link-right ${
            activeId === mediaItems.length - 1 ? 'disabled' : ''
          }`}
          onClick={(e) => {
            e.preventDefault();
            doLog(`/sc.details.main_image_swipe_${activeId + 1}`);
            setActiveId(activeId + 1);
          }}
        >
          <DetailIcon className="detail-icon-right" symbol="icon-right" width="30" height="30" />
        </div>
      </div>

      {/* 商品主图的hover引导词 + share功能*/}
      <div className="main-link">
        <div className="guide-words">
          {visGuideWords() &&
            (isHovering
              ? i18n['detail_pc_image_hover_expanded_view'] || 'Click image to open expanded view'
              : i18n['detail_fantasy_hover_to_zoom_in'] || 'Hover to zoom in')}
        </div>

        {/* 留share组件坑位，由sc-details异步加载 */}
        <div className="detail-main-share" />
      </div>
      {/* 商品主图的收藏功能 */}
      <div className="detail-favorite-container">
        <div className="detail-favorite" onClick={handleFavorite}>
          {isFavorite ? (
            <DetailIcon
              className="detail-icon-favorite-fill"
              symbol="icon-follow-fill"
              width="30"
              height="30"
            />
          ) : (
            <DetailIcon
              className="detail-icon-favorite"
              symbol="icon-follow"
              width="30"
              height="30"
            />
          )}
        </div>
      </div>
    </div>
  );
}
